import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Download, Trash2, PauseCircle, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { AriaConsentCard } from '@/components/aria/AriaConsentCard';

const Row = ({ id, label, hint, checked, onChange }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <div><Label htmlFor={id}>{label}</Label>{hint && <p className="text-xs text-muted-foreground">{hint}</p>}</div>
    <Switch id={id} checked={!!checked} onCheckedChange={onChange} data-testid={id} />
  </div>
);
const Box = ({ title, children, testid }) => <div className="rounded-2xl border bg-card p-4 space-y-1" data-testid={testid}><p className="font-semibold mb-1">{title}</p>{children}</div>;

const Reminders = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [when, setWhen] = useState('');
  const load = () => api.get('/aria/reminders').then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post('/aria/reminders', { text, remind_at: new Date(when).toISOString() }); setText(''); setWhen(''); load(); toast.success(t('aria.action.done')); }
    catch (e) { toast.error(e.response?.data?.detail || t('aria.error')); }
  };
  const del = async (id) => { await api.delete(`/aria/reminders/${id}`); load(); };
  return (
    <Box title={t('aria.settings.reminders')} testid="aria-settings-reminders">
      {items.map((r) => (
        <div key={r.id} className="flex items-center gap-2 text-sm py-1" data-testid={`aria-reminder-${r.id}`}>
          <span className="flex-1 break-words">{r.text}</span><span className="text-xs text-muted-foreground">{new Date(r.remind_at).toLocaleString()}</span>
          <button type="button" onClick={() => del(r.id)} aria-label={t('aria.action.cancel')} className="p-2"><X className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2 pt-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={200} placeholder={t('aria.settings.reminderText')} aria-label={t('aria.settings.reminderText')} className="min-h-[44px]" data-testid="aria-reminder-text" />
        <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} aria-label={t('aria.settings.reminderWhen')} className="min-h-[44px]" data-testid="aria-reminder-when" />
        <Button onClick={add} disabled={!text.trim() || !when} className="min-h-[44px] gap-1" data-testid="aria-reminder-add"><Plus className="h-4 w-4" />{t('aria.settings.add')}</Button>
      </div>
    </Box>
  );
};

export const AriaSettingsTab = ({ settings, onSettings, onHistoryDeleted }) => {
  const { t, i18n } = useTranslation();
  const [consents, setConsents] = useState([]);
  useEffect(() => { api.get('/aria/consent/history').then((r) => setConsents(r.data)).catch(() => {}); }, [settings?.consent]);
  if (!settings) return null;

  const patch = async (body) => {
    try { const { data } = await api.patch('/aria/settings', body); onSettings(data); }
    catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || t('aria.error')); }
  };
  const revoke = async () => { const { data } = await api.delete('/aria/consent', { params: { locale: i18n.language } }); onSettings(data); toast.success(t('aria.settings.revoked')); };
  const exportData = async () => {
    const { data } = await api.get('/aria/history/export');
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a'); a.href = url; a.download = 'aria-meus-dados.json'; a.click(); URL.revokeObjectURL(url);
  };
  const wipe = async () => { await api.delete('/aria/history'); toast.success(t('aria.settings.deleted')); onHistoryDeleted?.(); };
  const paused = settings.paused_until && new Date(settings.paused_until) > new Date();
  const hours = Array.from({ length: 24 }, (_, h) => String(h));

  return (
    <div className="space-y-4 pb-6" data-testid="aria-settings-tab">
      <Box title={t('aria.settings.assistant')} testid="aria-settings-main">
        <Row id="aria-toggle-enabled" label={t('aria.settings.enabled')} hint={t('aria.settings.enabledHint')} checked={settings.enabled} onChange={(v) => patch({ enabled: v })} />
        <div className="flex items-center justify-between gap-3 py-2">
          <p className="text-sm">{paused ? t('aria.settings.pausedUntil', { date: new Date(settings.paused_until).toLocaleDateString() }) : t('aria.settings.pauseHint')}</p>
          <Button size="sm" variant="outline" className="min-h-[40px] gap-1" onClick={() => patch({ pause_days: paused ? 0 : 7 })} data-testid="aria-pause-btn">
            <PauseCircle className="h-4 w-4" />{paused ? t('aria.settings.resume') : t('aria.settings.pause7')}
          </Button>
        </div>
      </Box>

      {!settings.consent ? <AriaConsentCard settings={settings} onChange={onSettings} /> : (
        <Box title={t('aria.settings.data')} testid="aria-settings-scopes">
          {['journey', 'checkins', 'courses', 'awa'].map((k) => (
            <Row key={k} id={`aria-scope-${k}`} label={t(`aria.scope.${k}`)} checked={settings.scopes[k]} onChange={(v) => patch({ scopes: { [k]: v } })} />
          ))}
          <Button variant="outline" size="sm" onClick={revoke} className="mt-2 min-h-[40px]" data-testid="aria-revoke-consent">{t('aria.settings.revoke')}</Button>
        </Box>
      )}

      <Box title={t('aria.settings.notifications')} testid="aria-settings-notify">
        {['medal_approach', 'anniversary', 'no_checkin', 'course_step'].map((k) => (
          <Row key={k} id={`aria-notify-${k}`} label={t(`aria.notify.${k}`)} checked={settings.notify[k]} onChange={(v) => patch({ notify: { [k]: v } })} />
        ))}
        <Row id="aria-daily-checkin" label={t('aria.settings.dailyCheckin')} checked={settings.daily_checkin_reminder.enabled}
          onChange={(v) => patch({ daily_checkin_reminder: { ...settings.daily_checkin_reminder, enabled: v } })} />
        {settings.daily_checkin_reminder.enabled && (
          <Input type="time" value={settings.daily_checkin_reminder.time} aria-label={t('aria.settings.dailyCheckin')} className="w-36 min-h-[44px]"
            onChange={(e) => e.target.value && patch({ daily_checkin_reminder: { enabled: true, time: e.target.value } })} data-testid="aria-daily-checkin-time" />
        )}
        <div className="flex flex-wrap items-center gap-2 pt-2 text-sm">
          <span>{t('aria.settings.quiet')}</span>
          {['quiet_start', 'quiet_end'].map((k) => (
            <Select key={k} value={String(settings[k])} onValueChange={(v) => patch({ [k]: Number(v) })}>
              <SelectTrigger className="w-20 min-h-[40px]" aria-label={k} data-testid={`aria-${k}`}><SelectValue /></SelectTrigger>
              <SelectContent>{hours.map((h) => <SelectItem key={h} value={h}>{h}h</SelectItem>)}</SelectContent>
            </Select>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{t('aria.settings.limit')}</p>
      </Box>

      <Reminders />

      <Box title={t('aria.settings.privacy')} testid="aria-settings-privacy">
        <div className="flex flex-wrap items-center gap-2 py-1 text-sm">
          <span>{t('aria.settings.retention')}</span>
          <Select value={String(settings.retention_days)} onValueChange={(v) => patch({ retention_days: Number(v) })}>
            <SelectTrigger className="w-44 min-h-[40px]" data-testid="aria-retention-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">{t('aria.settings.days', { n: 30 })}</SelectItem>
              <SelectItem value="90">{t('aria.settings.days', { n: 90 })}</SelectItem>
              <SelectItem value="0">{t('aria.settings.untilDelete')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={exportData} className="min-h-[40px] gap-1" data-testid="aria-export-btn"><Download className="h-4 w-4" />{t('aria.settings.export')}</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" className="min-h-[40px] gap-1" data-testid="aria-delete-history-btn"><Trash2 className="h-4 w-4" />{t('aria.settings.deleteAll')}</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>{t('aria.settings.deleteAll')}</AlertDialogTitle><AlertDialogDescription>{t('aria.settings.deleteConfirm')}</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>{t('aria.action.cancel')}</AlertDialogCancel><AlertDialogAction onClick={wipe} data-testid="aria-delete-history-confirm">{t('aria.action.confirm')}</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        <p className="text-xs text-muted-foreground pt-2">{t('aria.settings.policy')}</p>
        <p className="text-xs text-muted-foreground">{t('aria.settings.provider')}</p>
      </Box>

      <Box title={t('aria.settings.consentHistory')} testid="aria-consent-history">
        {consents.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : consents.map((c) => (
          <p key={c.id} className="text-xs flex justify-between gap-2"><span>{t(`aria.settings.consent_${c.action}`)} · v{c.text_version} · {c.locale}</span><span className="text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span></p>
        ))}
      </Box>
    </div>
  );
};
